import { algoliasearch } from 'algoliasearch'
import { UiState } from 'instantsearch.js'
import { history } from 'instantsearch.js/es/lib/routers'
import type { RouteState } from 'src/components/algolia/algolia'
import { mainIndex } from './algoliaSearchConfig'
import { facetsMap, getIsPreAppliedFilter, scalarKeys } from './utils'

export const preAppliedFiltersKey = 'algolia:preAppliedFilters'

const normalizeToArray = (raw: unknown) => {
  if (raw === undefined || raw === null) return []
  // if raw is an array of strings
  if (Array.isArray(raw)) {
    return raw.map(String).map(decodeURIComponent).filter(Boolean)
  }
  // if raw is a string; it can be 'a' or 'a,b'
  if (typeof raw === 'string') {
    return String(raw)
      .split(',')
      .map((value) => decodeURIComponent(value.trim()))
      .filter(Boolean)
  }
  // if raw is an object, we return it as is
  return raw as []
}

const normalizeForRetroCompatibility = (raw: string) => {
  // If the facet value is not mapped in facetsMap, we normalize it to be compatible with the old versions of URLs
  return raw.replaceAll('-', ' ')
  // .replace(/\b(e|di|da|a|con|per|su|in|tra|fra)\b/gi, ' ')
}

export const getRouting = (preAppliedFilters: Record<string, string[]>) => {
  return {
    router: history<RouteState>({
      createURL({ qsModule, routeState, location }) {
        const queryString = qsModule.stringify(routeState, {
          addQueryPrefix: true,
          arrayFormat: 'repeat',
        })
        return location.pathname + queryString
      },
      parseURL({ qsModule, location }) {
        const parsed = qsModule.parse(location.search.slice(1))

        const routeState = {} as RouteState

        // We need all this processing to handle the case where in the URL there are multiple facets of the same filter divided by a comma (e.g. ?brand=ACANA,MONGE), so we can handle both this case and the standard case (e.g. ?brand=ACANA&brand=MONGE)
        Object.keys(parsed).forEach((key) => {
          const value = parsed[key]

          if (value && typeof value === 'object' && !Array.isArray(value)) {
            routeState[key] = Object.keys(value).reduce((acc, subKey) => {
              const subVal = value[subKey]
              if (typeof subVal === 'string') {
                acc[subKey] = decodeURIComponent(subVal)
              } else {
                acc[subKey] = normalizeToArray(subVal)
              }
              return acc
            }, {} as Record<string, string | string[]>)
          } else if (scalarKeys.has(key)) {
            routeState[key] =
              typeof value === 'string' ? decodeURIComponent(value) : value
          } else {
            const normalizedValue = normalizeToArray(value)
            if (normalizedValue) {
              routeState[key] = normalizedValue
            }
          }
        })

        return routeState
      },
    }),

    stateMapping: {
      routeToState: (routeState: RouteState) => {
        let newState: UiState = {}
        if (preAppliedFilters) {
          Object.entries(preAppliedFilters).forEach(([key, value]) => {
            newState = {
              [mainIndex]: {
                ...routeState,
                refinementList: {
                  ...newState[mainIndex]?.refinementList, // This is mandatory if preAppliedFilters has more than one key
                  ...(key && key !== 'q' && { [key]: value }), // We add in the uiState the pre-applied filters coming from the url and remap them
                },
              },
            }
          })
        }

        Object.entries(routeState).forEach(([key, value]) => {
          const previousValue = newState[mainIndex]?.refinementList?.[key || '']
          const isOldPriceFilter = key === 'prezzo'
          const oldPriceFilterValue = isOldPriceFilter
            ? value[0].split('----')
            : undefined
          const fixedKey = isOldPriceFilter ? 'range' : key
          const fixedValue = facetsMap[value]
            ? facetsMap[value]
            : isOldPriceFilter
            ? {
                selling_price: `${oldPriceFilterValue[0]}:${oldPriceFilterValue[1]}`,
              }
            : Array.isArray(value)
            ? value.map(normalizeForRetroCompatibility)
            : value
          const isAlreadyPresent = previousValue?.find((prevVal) => {
            return Array.isArray(fixedValue)
              ? prevVal.toLowerCase() === fixedValue?.[0]?.toLowerCase()
              : prevVal.toLowerCase() === fixedValue.toLowerCase()
          })

          newState = {
            [mainIndex]: {
              ...newState[mainIndex],
              refinementList: {
                ...newState[mainIndex]?.refinementList,
                ...(fixedKey &&
                  !scalarKeys.has(fixedKey) && {
                    [fixedKey]: [
                      ...(previousValue ?? []),
                      ...(!isAlreadyPresent
                        ? Array.isArray(fixedValue)
                          ? fixedValue
                          : [fixedValue]
                        : []),
                    ],
                  }), // We add in the uiState the filters coming from the url that are remapped
              },
            },
          }
        })

        const hasOldPriceFilter = routeState.hasOwnProperty('prezzo')
        const oldPriceFilterValue = hasOldPriceFilter
          ? routeState['prezzo']?.[0]?.split('----')
          : undefined
        const oldPriceValueFixed = hasOldPriceFilter
          ? `${oldPriceFilterValue[0]}:${oldPriceFilterValue[1]}`
          : undefined

        newState = {
          // This to change "sortBy" to "sort" in the url
          [mainIndex]: {
            ...newState[mainIndex],
            toggle: routeState.hasOwnProperty('private_label')
              ? { private_label: routeState.private_label }
              : undefined,
            page:
              typeof routeState.page !== 'undefined'
                ? Number(routeState.page) + 1
                : undefined,
            sortBy: routeState.sort,
            range: hasOldPriceFilter
              ? { selling_price: oldPriceValueFixed || '' }
              : routeState.range_price
              ? { selling_price: routeState.range_price[0] }
              : undefined,
          },
        }

        return newState
      },
      stateToRoute: (uiState: UiState) => {
        const { configure, ...restUiState } = uiState[mainIndex] // We exclude configure from the url
        let newState: RouteState = restUiState
        const entries = !configure?.query // We need to let the ?q param persist in the url in SERP and to do so we must treat it as pre-applied filter
          ? newState.refinementList || {}
          : { ...newState.refinementList, q: configure?.query }
        Object.entries(entries).forEach(([key, value]) => {
          const isPreAppliedFilter = getIsPreAppliedFilter(preAppliedFilters, {
            [key]: value as string[],
          }) // We check if the filter key is pre applied
          const valueToAdd = Array.isArray(value)
            ? value.filter((val) => {
                const mappedPreAppliedValues = preAppliedFilters[key]?.map(
                  (preAppVal) => preAppVal.toLowerCase()
                )
                return !mappedPreAppliedValues?.includes(val.toLowerCase())
              })
            : value
          if (!isPreAppliedFilter || key === 'q') {
            // If it's not pre applied, we add it to the url
            newState = {
              ...newState,
              [key]: valueToAdd,
              refinementList: {},
            }
          } else {
            // Otherwise, we don't add it to the url
            newState = {
              ...newState,
              [key]: [],
              refinementList: {},
            }
          }
        })

        newState = {
          // This to change "sortBy" to "sort" in the url and to remove "toggle" from the url
          ...newState,
          sortBy: undefined,
          toggle: undefined,
          sort: newState.sortBy,
          page:
            typeof newState.page !== 'undefined'
              ? Number(newState.page) - 1
              : undefined,
          range: undefined,
          range_price:
            typeof newState?.range?.selling_price !== 'undefined'
              ? newState.range.selling_price
              : undefined,
        }

        return newState
      },
    },
  }
}

export const searchClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID ?? '',
  process.env.GATSBY_ALGOLIA_API_KEY ?? ''
)
